import { useState, type CSSProperties, type ReactNode } from 'react';
import { useI18n } from '../lib/i18n';
import type { ChartIndicators, ChartPayload, Drawing } from '../lib/drawings';
import { newId } from '../lib/drawings';
import { autoLevels, autoTrends, orderBlockBreakers, orderBlockHints } from '../lib/indicators';
import type { Candle } from '@termix/shared';

type Props = {
  payload: ChartPayload;
  onPayloadChange: (next: ChartPayload) => void;
  onSave: () => void;
  /** Current candles — used to materialize structure hints as drawings. */
  candles?: Candle[];
  /** Timeframe label stamped into saved drawing titles (e.g. 15m). */
  timeframe?: string;
  onNotice?: (msg: string) => void;
};

const Section = ({
  title,
  open,
  onToggle,
  children,
  hint,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  hint?: string;
}) => (
  <div style={{
    marginBottom: 10, borderRadius: 10, border: '1px solid var(--line)',
    background: 'var(--panel-2)', overflow: 'hidden',
  }}>
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px', border: 'none', background: 'transparent',
        color: 'var(--gold)', fontWeight: 700, fontSize: 12, letterSpacing: '0.04em',
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ opacity: 0.7, width: 12 }}>{open ? '▾' : '▸'}</span>
      <span style={{ flex: 1 }}>{title}</span>
    </button>
    {open ? (
      <div style={{ padding: '0 12px 12px' }}>
        {hint ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginBottom: 8 }}>{hint}</div>
        ) : null}
        {children}
      </div>
    ) : null}
  </div>
);

const row: CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 13, color: 'var(--text)',
};
const inputStyle: CSSProperties = {
  width: '100%', marginTop: 4, marginBottom: 8, padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--line)', background: 'var(--panel)', color: 'var(--text)',
};
const labelStyle: CSSProperties = { display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 4 };
const ghostBtn: CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--line)',
  background: 'var(--panel)', color: 'var(--text)', cursor: 'pointer', fontSize: 13,
};
export default function IndicatorsPanel({
  payload, onPayloadChange, onSave, candles = [], timeframe = '15m', onNotice,
}: Props) {
  const { t } = useI18n();
  const ind = payload.indicators || {};
  const [open, setOpen] = useState(() => {
    try {
      const raw = sessionStorage.getItem('termix_ind_sections');
      if (raw) {
        const parsed = JSON.parse(raw) as { basics?: boolean; zones?: boolean; structure?: boolean };
        return {
          basics: Boolean(parsed.basics),
          zones: Boolean(parsed.zones),
          structure: parsed.structure !== false,
        };
      }
    } catch { /* ignore */ }
    return { basics: false, zones: false, structure: true };
  });
  const toggleSection = (key: 'basics' | 'zones' | 'structure') => {
    setOpen((s) => {
      const next = { ...s, [key]: !s[key] };
      try { sessionStorage.setItem('termix_ind_sections', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const patch = (partial: Partial<ChartIndicators>) => {
    onPayloadChange({
      ...payload,
      indicators: { ...ind, ...partial },
    });
  };

  const sens = Math.max(0, Math.min(100, Number(ind.hintSensitivity ?? 50)));

  const saveStructureAsDrawings = () => {
    if (candles.length < 20) {
      onNotice?.(t.fewCandles('—', timeframe));
      return;
    }
    const sorted = [...candles].sort((a, b) => a.time - b.time);
    const next: Drawing[] = [...payload.drawings];
    let added = 0;
    const tfTag = String(timeframe || '15m');
    if (ind.autoLevels) {
      for (const lvl of autoLevels(sorted, ind.autoLevelsCount ?? 6, 2, sens)) {
        next.push({
          id: newId(),
          type: 'hline',
          price: lvl.price,
          color: lvl.kind === 'high' ? '#e25b5b' : lvl.kind === 'low' ? '#3dba7a' : '#c9a227',
          title: `L·${tfTag}`,
        });
        added += 1;
      }
    }
    if (ind.autoTrends) {
      for (const tr of autoTrends(sorted, ind.autoTrendsCount ?? 2, 2, sens)) {
        next.push({
          id: newId(),
          type: 'trend',
          t1: tr.t1, p1: tr.p1, t2: tr.t2, p2: tr.p2,
          color: tr.kind === 'up' ? '#3dba7a' : '#e25b5b',
        });
        added += 1;
      }
    }
    if (ind.orderBlocks) {
      for (const z of orderBlockHints(sorted, ind.orderBlocksCount ?? 5, sens, null)) {
        const tag = z.side === 'bull' ? 'OB↑' : 'OB↓';
        const st = z.status && z.status !== 'fresh' ? `·${z.status}` : '';
        next.push({
          id: newId(),
          type: 'hline',
          price: z.priceHigh,
          color: z.side === 'bull' ? '#3dba7a' : '#e25b5b',
          title: `${tag}hi·${tfTag}${st}`,
        });
        next.push({
          id: newId(),
          type: 'hline',
          price: z.priceLow,
          color: z.side === 'bull' ? '#3dba7a' : '#e25b5b',
          title: `${tag}lo·${tfTag}${st}`,
        });
        if (z.midPrice != null) {
          next.push({
            id: newId(),
            type: 'hline',
            price: z.midPrice,
            color: '#c9a227',
            title: `${tag}CE·${tfTag}`,
          });
        }
        added += 1;
      }
    }
    if (ind.obBreakers) {
      for (const z of orderBlockBreakers(sorted, ind.obBreakersCount ?? 4, sens, null)) {
        const tag = z.side === 'bull' ? 'BRK↑' : 'BRK↓';
        next.push({
          id: newId(),
          type: 'hline',
          price: z.priceHigh,
          color: z.side === 'bull' ? 'rgba(61,186,122,0.55)' : 'rgba(226,91,91,0.55)',
          title: `${tag}hi·${tfTag}`,
        });
        next.push({
          id: newId(),
          type: 'hline',
          price: z.priceLow,
          color: z.side === 'bull' ? 'rgba(61,186,122,0.55)' : 'rgba(226,91,91,0.55)',
          title: `${tag}lo·${tfTag}`,
        });
        added += 1;
      }
    }
    if (!added) {
      onNotice?.(t.indSaveStructureHint);
      return;
    }
    onPayloadChange({ ...payload, drawings: next });
    onNotice?.(t.indSaveStructureDone(added));
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>{t.indicatorsTitle}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginBottom: 12 }}>
        {t.indicatorsHintOnly}
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginBottom: 12 }}>
        {t.indHistoryScrollHint}
      </div>

      <Section
        title={t.indSectionStructure}
        open={open.structure}
        onToggle={() => toggleSection('structure')}
        hint={t.indStructureHint}
      >
        <label style={labelStyle}>{t.indSensitivity}</label>
        <input
          type="range" min={0} max={100} step={5}
          value={sens}
          onChange={(e) => patch({ hintSensitivity: Number(e.target.value) })}
          style={{ width: '100%', marginTop: 4, marginBottom: 4 }}
        />
        <div style={{
          display: 'flex', justifyContent: 'space-between', fontSize: 10,
          color: 'var(--muted)', marginBottom: 10,
        }}>
          <span>{t.indSensitivityStrict}</span>
          <span>{sens}</span>
          <span>{t.indSensitivityLoose}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.35, marginBottom: 10 }}>
          {t.indSensitivityHint}
        </div>

        <label style={row}>
          <input
            type="checkbox"
            checked={Boolean(ind.autoLevels)}
            onChange={(e) => patch({ autoLevels: e.target.checked })}
          />
          {t.indAutoLevels}
        </label>
        {ind.autoLevels ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.35, marginBottom: 8 }}>
            {t.indAutoLevelsHint}
          </div>
        ) : null}
        <label style={labelStyle}>{t.indLevelsCount}</label>
        <input
          type="number" min={2} max={12}
          value={ind.autoLevelsCount ?? 6}
          disabled={!ind.autoLevels}
          onChange={(e) => patch({ autoLevelsCount: Math.max(2, Math.min(12, Number(e.target.value) || 6)) })}
          style={{ ...inputStyle, opacity: ind.autoLevels ? 1 : 0.45 }}
        />

        <label style={row}>
          <input
            type="checkbox"
            checked={Boolean(ind.autoTrends)}
            onChange={(e) => patch({ autoTrends: e.target.checked })}
          />
          {t.indAutoTrends}
        </label>
        <label style={labelStyle}>{t.indTrendsCount}</label>
        <input
          type="number" min={1} max={4}
          value={ind.autoTrendsCount ?? 2}
          disabled={!ind.autoTrends}
          onChange={(e) => patch({ autoTrendsCount: Math.max(1, Math.min(4, Number(e.target.value) || 2)) })}
          style={{ ...inputStyle, opacity: ind.autoTrends ? 1 : 0.45 }}
        />

        <label style={row}>
          <input
            type="checkbox"
            checked={Boolean(ind.orderBlocks)}
            onChange={(e) => patch({ orderBlocks: e.target.checked })}
          />
          {t.indOrderBlocks}
        </label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, opacity: ind.orderBlocks ? 1 : 0.45 }}>
          <input
            type="number" min={1} max={8}
            value={ind.orderBlocksCount ?? 5}
            disabled={!ind.orderBlocks}
            onChange={(e) => patch({ orderBlocksCount: Math.max(1, Math.min(8, Number(e.target.value) || 5)) })}
            title={t.indBlocksCount}
            style={{ ...inputStyle, width: 64, margin: 0 }}
          />
          <div style={{ display: 'flex', gap: 6, flex: 1 }}>
            {([
              ['annotate', t.indObBookAnnotate] as const,
              ['widen', t.indObBookWiden] as const,
            ]).map(([id, label]) => {
              const active = (ind.orderBlocksBook || 'annotate') === id;
              return (
                <button
                  key={id}
                  type="button"
                  disabled={!ind.orderBlocks}
                  onClick={() => patch({ orderBlocksBook: id })}
                  style={{
                    flex: 1, padding: '7px 8px', borderRadius: 8, cursor: ind.orderBlocks ? 'pointer' : 'default',
                    fontSize: 12, fontWeight: active ? 700 : 500, textAlign: 'center',
                    border: `1px solid ${active ? 'var(--gold)' : 'var(--line)'}`,
                    color: active ? 'var(--gold)' : 'var(--text)',
                    background: 'var(--panel)',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.35, marginBottom: 10 }}>
          {t.indObBookModeHint}
        </div>

        <label style={row}>
          <input
            type="checkbox"
            checked={Boolean(ind.obBreakers)}
            onChange={(e) => patch({ obBreakers: e.target.checked })}
          />
          {t.indObBreakers}
        </label>
        <label style={labelStyle}>{t.indObBreakersCount}</label>
        <input
          type="number" min={1} max={8}
          value={ind.obBreakersCount ?? 4}
          disabled={!ind.obBreakers}
          onChange={(e) => patch({ obBreakersCount: Math.max(1, Math.min(8, Number(e.target.value) || 4)) })}
          style={{ ...inputStyle, opacity: ind.obBreakers ? 1 : 0.45 }}
        />
        <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.35, marginBottom: 10 }}>
          {t.indObBreakersHint}
        </div>

        <button
          type="button"
          style={{ ...ghostBtn, marginTop: 4, borderColor: 'var(--gold)', color: 'var(--gold)' }}
          onClick={saveStructureAsDrawings}
          title={t.indSaveStructureHint}
        >
          {t.indSaveStructure}
        </button>
        <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.35, marginTop: 10 }}>
          {t.indPaneDragHint}
        </div>
      </Section>

      <Section
        title={t.indSectionZones}
        open={open.zones}
        onToggle={() => toggleSection('zones')}
        hint={t.indZonesHint}
      >
        <label style={row}>
          <input
            type="checkbox"
            checked={Boolean(ind.rsiZones)}
            onChange={(e) => patch({ rsiZones: e.target.checked })}
          />
          {t.indRsiZones}
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={labelStyle}>{t.indRsiOb}</label>
            <input
              type="number" min={50} max={95} step={1}
              value={ind.rsiOb ?? 70}
              disabled={!ind.rsiZones}
              onChange={(e) => patch({ rsiOb: Number(e.target.value) || 70 })}
              style={{ ...inputStyle, opacity: ind.rsiZones ? 1 : 0.45 }}
            />
          </div>
          <div>
            <label style={labelStyle}>{t.indRsiOs}</label>
            <input
              type="number" min={5} max={50} step={1}
              value={ind.rsiOs ?? 30}
              disabled={!ind.rsiZones}
              onChange={(e) => patch({ rsiOs: Number(e.target.value) || 30 })}
              style={{ ...inputStyle, opacity: ind.rsiZones ? 1 : 0.45 }}
            />
          </div>
        </div>
      </Section>

      <Section
        title={t.indSectionBasics}
        open={open.basics}
        onToggle={() => toggleSection('basics')}
      >
        {([
          ['volume', 'Volume', ind.volume !== false],
          ['sma', 'SMA', ind.sma !== false],
          ['ema', 'EMA', Boolean(ind.ema)],
          ['rsi', 'RSI', ind.rsi !== false],
          ['barCountdown', t.barCountdown, ind.barCountdown !== false],
        ] as const).map(([key, label, checked]) => (
          <label key={key} style={row} title={key === 'barCountdown' ? t.barCountdownHint : undefined}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => patch({ [key]: e.target.checked })}
            />
            {label}
          </label>
        ))}
        {ind.barCountdown !== false ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, lineHeight: 1.4 }}>
            {t.barCountdownHint}
          </div>
        ) : null}
        <label style={labelStyle}>SMA</label>
        <input
          type="number" min={2} max={200} value={ind.smaLen || 20}
          onChange={(e) => patch({ smaLen: Number(e.target.value) || 20 })}
          style={inputStyle}
        />
        <label style={labelStyle}>EMA</label>
        <input
          type="number" min={2} max={400} value={ind.emaLen || 50}
          onChange={(e) => patch({ emaLen: Number(e.target.value) || 50 })}
          style={inputStyle}
        />
        <label style={labelStyle}>RSI</label>
        <input
          type="number" min={2} max={100} value={ind.rsiLen || 14}
          onChange={(e) => patch({ rsiLen: Number(e.target.value) || 14 })}
          style={inputStyle}
        />
      </Section>

      <button type="button" style={{ ...ghostBtn, marginTop: 4 }} onClick={onSave}>
        {t.saveWithChart}
      </button>
    </div>
  );
}
